import React, { useState, useEffect } from 'react';
import { Input, Button, Select, Alert, message } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './PersonalInformation.css';

const { Option } = Select;

const PersonalInformation = () => {
  const [fullNames, setFullNames] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [zipCode, setZipCode] = useState('');
  const [state, setState] = useState('');
  const [amenities, setAmenities] = useState([]);
  const [availableSpace, setAvailableSpace] = useState('');
  const [beds, setBeds] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch personal info
  const fetchPersonalInfo = async () => {
    setLoading(true);
    try {
      console.log('Envoi de la requête vers /backend/admin_get_personal_info.php');
      const response = await fetch('/backend/admin_get_personal_info.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      console.log('Statut HTTP:', response.status);
      const text = await response.text();
      console.log('Réponse brute:', text);
      let data;
      try {
        data = JSON.parse(text);
        console.log('Données parsées:', data);
      } catch (e) {
        console.error('Erreur de parsing JSON:', e, 'Texte reçu:', text);
        throw new Error('Réponse non-JSON');
      }
      if (response.ok) {
        setFullNames(data.full_names || '');
        setCategory(data.category || '');
        setZipCode(data.zip_code || '');
        setState(data.state || '');
        setAmenities(data.amenities || []);
        setAvailableSpace(data.available_space || '');
        setBeds(data.beds || '');
        setLocation(data.location || '');
        setError(null);
      } else {
        if (response.status === 403) {
          console.log('Erreur 403: Session expirée');
          message.error('Session expirée. Veuillez vous reconnecter.');
          navigate('/adminlogin');
        } else {
          setError(data.error || `Erreur HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Erreur réseau:', error.message);
      setError(`Erreur réseau: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories directly from categories table
  const fetchCategories = async () => {
    try {
      const response = await fetch('/backend/get_categories.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        setCategories(data.categories);
      } else {
        message.error(data.error || 'Erreur lors de la récupération des catégories');
      }
    } catch (error) {
      console.error('Erreur réseau pour les catégories:', error);
      message.error('Erreur réseau lors de la récupération des catégories');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      console.log('Envoi des données vers /backend/admin_update_personal_info.php');
      const response = await fetch('/backend/admin_update_personal_info.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          full_names: fullNames,
          category,
          zip_code: zipCode,
          state,
          amenities,
          available_space: availableSpace,
          beds,
          location,
        }),
      });
      console.log('Statut HTTP:', response.status);
      const data = await response.json();
      console.log('Réponse:', data);
      if (response.ok) {
        message.success('Informations mises à jour avec succès');
        setError(null);
      } else {
        if (response.status === 403) {
          message.error('Session expirée. Veuillez vous reconnecter.');
          navigate('/adminlogin');
        } else {
          setError(data.error || 'Erreur lors de la mise à jour');
        }
      }
    } catch (error) {
      console.error('Erreur réseau:', error.message);
      setError(`Erreur réseau: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    alert('Edit clicked! (Placeholder action)');
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this information?')) {
      setFullNames('');
      setCategory('');
      setZipCode('');
      setState('');
      setAmenities([]);
      setAvailableSpace('');
      setBeds('');
      setLocation('');
      alert('Information deleted successfully! (Placeholder action)');
    }
  };

  useEffect(() => {
    fetchPersonalInfo();
    fetchCategories();
  }, []);

  return (
    <div className="personal-info-container">
      <h2 className="section-title">Personal Information</h2>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 20 }} />}
      <div className="info-form">
        <div className="form-row">
          <div className="form-group">
            <label>Full names</label>
            <Input
              placeholder="Enter full names"
              value={fullNames}
              onChange={(e) => setFullNames(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            <Select
              placeholder="Select category"
              value={category}
              onChange={(value) => setCategory(value)}
              allowClear
              disabled={loading}
            >
              {categories.map((cat) => (
                <Option key={cat.id} value={cat.name}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Zip Code</label>
            <Input
              placeholder="Enter zip code"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label>State</label>
            <Select
              placeholder="Select state"
              value={state}
              onChange={(value) => setState(value)}
              allowClear
              disabled={loading}
            >
              <Option value="State 1">State 1</Option>
              <Option value="State 2">State 2</Option>
              <Option value="State 3">State 3</Option>
            </Select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Amenities</label>
            <Select
              mode="multiple"
              placeholder="Select amenities"
              value={amenities}
              onChange={(value) => setAmenities(value)}
              allowClear
              disabled={loading}
            >
              <Option value="Wi-Fi">Wi-Fi</Option>
              <Option value="Parking">Parking</Option>
              <Option value="Pool">Pool</Option>
            </Select>
          </div>
          <div className="form-group">
            <label>Available Space</label>
            <Input
              placeholder="Enter available space"
              value={availableSpace}
              onChange={(e) => setAvailableSpace(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Beds</label>
            <Input
              placeholder="Enter number of beds"
              value={beds}
              onChange={(e) => setBeds(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label>Location</label>
            <Input
              placeholder="Enter location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
        <div className="form-actions">
          <Button type="primary" onClick={handleSave} loading={loading}>
            Save
          </Button>
          <Button icon={<EditOutlined />} onClick={handleEdit} style={{ marginLeft: 8 }} disabled={loading} />
          <Button icon={<DeleteOutlined />} onClick={handleDelete} style={{ marginLeft: 8 }} danger disabled={loading} />
        </div>
      </div>
    </div>
  );
};

export default PersonalInformation;